import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Company } from "@/types/company";
import {
  Building2,
  Globe,
  Phone,
  Mail,
  MapPin,
  Users,
  Calendar,
  Clock,
  Activity,
  Trash2,
  Edit,
  ExternalLink,
  Briefcase,
  FileText,
  UserPlus,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CreateContactModal } from "@/components/contacts/CreateContactModal";
import { useContacts } from "@/hooks/useContacts";
import { contactoAPI } from "@/services/contactoService";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface CompanyDetailProps {
  company: Company | null;
  isOpen: boolean;
  onClose: () => void;
  onContactClick: (contact: any) => void;
  onUpdate: (id: string, data: Partial<Company>) => void;
  onDelete: (id: string) => void;
  detailLoading?: boolean;
}

const getIndustryName = (company: Company): string => {
  if (!company) return "Sin industria";
  if (typeof company.industry === "string") return company.industry;
  if (company.industry && typeof company.industry === "object") {
    const industry = company.industry as any;
    return industry.name || company.industry_id || "Sin industria";
  }
  return company.industry_id || "Sin industria";
};

const safeFormatDate = (
  date: Date | string | undefined | null,
  formatString: string = "dd MMM yyyy",
) => {
  if (!date) return "No disponible";
  try {
    let dateObj: Date;
    if (
      typeof date === "string" &&
      date.includes(" ") &&
      !date.includes("T") &&
      !date.includes("Z")
    ) {
      dateObj = new Date(date.replace(" ", "T") + "Z");
    } else {
      dateObj = typeof date === "string" ? new Date(date) : date;
    }
    if (isNaN(dateObj.getTime())) return "Fecha inválida";
    return format(dateObj, formatString, { locale: es });
  } catch (error) {
    return "Error en fecha";
  }
};

export function CompanyDetail({
  company,
  isOpen,
  onClose,
  onContactClick,
  onUpdate,
  onDelete,
  detailLoading = false,
}: CompanyDetailProps) {
  const { toast } = useToast();
  const { company: authCompany } = useAuth();
  const { statuses: customerStatuses, refreshContacts } = useContacts();
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] =
    useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!company) return null;

  const handleCreateContact = async (contact: any) => {
    try {
      setSaving(true);
      const createData = {
        name: contact.name || `${contact.firstName} ${contact.lastName}`.trim(),
        position: contact.position || "",
        phone: contact.phone || "",
        email: contact.email || "",
        client_company_id: company.id,
        contact_status_id: contact.contact_status_id || contact.statusId,
        notes: contact.notes || "",
      };

      await contactoAPI.createContacto(authCompany!.slug, createData);

      toast({
        title: "Contacto creado",
        description: "El contacto ha sido creado exitosamente",
      });

      setIsCreateContactModalOpen(false);
      await refreshContacts();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.body?.error ||
        error.response?.data?.body?.message ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "No se pudo guardar el contacto";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    onDelete(company.id);
    setShowDeleteDialog(false);
  };

  const handleEdit = () => {
    onUpdate(company.id, {
      name: company.name,
      industry_id: company.industry_id,
      size: company.size,
      phone: company.phone,
      website: company.website,
      address: company.address,
      logo_url: company.logo_url || "",
      notes: company.notes || "",
      parent_company_id: company.parent_company_id || null,
    });
  };

  const companyIndustryName = getIndustryName(company);

  const getSizeDisplay = (size: string) => {
    if (size === "5001-10000") return "Más de 5000 empleados";

    const match = size.match(/(\d+)\s*-?\s*(\d+)?/);
    if (match) {
      const [, min, max] = match;
      if (max) return `${parseInt(min)}-${parseInt(max)} empleados`;
    }
    return size;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col bg-slate-50">
        <div className="bg-white border-b border-slate-100 p-4 sm:p-5 sm:px-6 z-10 relative">
          <DialogHeader>
            <div className="flex items-center gap-4">
              <Avatar className="h-14 w-14 shadow-sm border-2 border-white ring-1 ring-slate-100">
                <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-xl font-bold shadow-inner">
                  {company.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="text-left flex-1">
                <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                  {company.name}
                </DialogTitle>
                <DialogDescription className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className="inline-flex items-center text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                    <Briefcase className="h-3 w-3 mr-1 text-indigo-500" />
                    {companyIndustryName}
                  </span>
                  <span className="inline-flex items-center text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                    <Users className="h-3 w-3 mr-1 text-blue-500" />
                    {getSizeDisplay(company.size)}
                  </span>
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-indigo-500" />
                <h3 className="text-lg font-bold text-slate-800 tracking-tight">Información de Contacto</h3>
              </div>
              <div className="p-5 space-y-4">
                {company.phone && (
                  <div className="flex items-start space-x-3 group">
                    <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100">
                      <Phone className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Teléfono</p>
                      <p className="font-medium text-slate-800">
                        {company.phone}
                      </p>
                    </div>
                  </div>
                )}

                {company.website && (
                  <div className="flex items-start space-x-3 group">
                    <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100">
                      <Globe className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="pt-0.5">
                      <p className="text-xs text-slate-500 mb-0.5 font-medium uppercase tracking-wider">Sitio Web</p>
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center"
                      >
                        {company.website.replace(/^https?:\/\//, "")}
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </div>
                  </div>
                )}

                {company.address && (
                  <div className="flex items-start space-x-3 group">
                    <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100">
                      <MapPin className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="pt-0.5">
                      <p className="text-xs text-slate-500 mb-0.5 font-medium uppercase tracking-wider">Dirección</p>
                      <p className="font-medium text-slate-800 leading-tight">{company.address}</p>
                    </div>
                  </div>
                )}

                {company.email && (
                  <div className="flex items-start space-x-3 group">
                    <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100">
                      <Mail className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="pt-0.5">
                      <p className="text-xs text-slate-500 mb-0.5 font-medium uppercase tracking-wider">
                        Correo Electrónico
                      </p>
                      <p className="font-medium text-slate-800 leading-tight">
                        {company.email}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Document Adjunto */}
            {detailLoading ? (
              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">Documento Adjunto</h3>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 animate-pulse">
                    <div className="bg-slate-200 rounded-lg h-10 w-10" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-slate-200 rounded w-3/4" />
                      <div className="h-2 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              </div>
            ) : company.rtu_url && (() => {
              // Extract filename from URL for display (strip query params first)
              const urlPath = (company.rtu_url as string).split('?')[0];
              let displayName = urlPath.split('/').pop() || 'Documento Adjunto';
              try { displayName = decodeURIComponent(displayName); } catch {}
              
              return (
                <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-indigo-500" />
                    <h3 className="text-lg font-bold text-slate-800 tracking-tight">Documento Adjunto</h3>
                  </div>
                  <div className="p-5">
                    <a
                      href={company.rtu_url as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-slate-50/50 hover:bg-indigo-50/30 transition-colors rounded-xl border border-slate-100 group no-underline"
                    >
                      <div className="flex items-center space-x-3 overflow-hidden">
                        <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-100">
                          <FileText className="h-6 w-6 text-indigo-500 flex-shrink-0" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-700 truncate max-w-[200px]">{displayName}</p>
                          <p className="text-xs text-slate-400 mt-0.5">Haz clic para abrir</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-indigo-600 group-hover:text-indigo-800 transition-colors">
                        <ExternalLink className="h-4 w-4" />
                        <span className="text-sm font-medium">Ver</span>
                      </div>
                    </a>
                  </div>
                </div>
              );
            })()}

            {company.notes && (
              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">Notas</h3>
                </div>
                <div className="p-5">
                  <div className="bg-amber-50/50 border border-amber-100/50 p-4 rounded-xl">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {company.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                <Activity className="h-5 w-5 mr-2 text-indigo-500" />
                <h3 className="text-lg font-bold text-slate-800 tracking-tight">Información del Sistema</h3>
              </div>
              <div className="p-5">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50/50 rounded-xl border border-slate-100/50">
                    <div className="flex items-center space-x-2">
                      <div className="bg-white p-1.5 rounded-lg border border-slate-100 shadow-sm">
                        <Calendar className="h-4 w-4 text-slate-400" />
                      </div>
                      <span className="text-sm font-medium text-slate-600">Creada</span>
                    </div>
                    <span className="font-medium text-slate-800">
                      {safeFormatDate(
                        (company as any).created_at ||
                          (company as any).createdAt ||
                          (company as any).fecha_creacion,
                        "dd/MM/yyyy HH:mm",
                      )}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50/50 rounded-xl border border-slate-100/50">
                    <div className="flex items-center space-x-2">
                      <div className="bg-white p-1.5 rounded-lg border border-slate-100 shadow-sm">
                        <Clock className="h-4 w-4 text-slate-400" />
                      </div>
                      <span className="text-sm font-medium text-slate-600">Actualizada</span>
                    </div>
                    <span className="font-medium text-slate-800 text-sm">
                      {safeFormatDate(
                        (company as any).updated_at ||
                          (company as any).updatedAt ||
                          (company as any).fecha_actualizacion,
                        "dd/MM/yyyy HH:mm",
                      )}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50/50 rounded-xl border border-slate-100/50">
                    <div className="flex items-center space-x-2">
                      <div className="bg-white p-1.5 rounded-lg border border-slate-100 shadow-sm">
                        <Activity className="h-4 w-4 text-slate-400" />
                      </div>
                      <span className="text-sm font-medium text-slate-600">Última Actividad</span>
                    </div>
                    <span className="font-medium text-slate-800 text-sm">
                      {safeFormatDate(
                        (company as any).last_activity_at ||
                          (company as any).lastActivityAt,
                        "dd/MM/yyyy HH:mm",
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {company.parent_company && (
              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <Building2 className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">Empresa Matriz</h3>
                </div>
                <div className="p-5">
                  <div className="flex items-center space-x-3 p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/50">
                    <Avatar className="h-10 w-10 border border-white shadow-sm ring-1 ring-slate-100">
                      <AvatarImage
                        src={company.parent_company.logo_url || ""}
                      />
                      <AvatarFallback className="bg-indigo-100 text-indigo-700 font-bold">
                        {company.parent_company.name.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900 leading-tight">
                        {company.parent_company.name}
                      </p>
                      <p className="text-sm text-slate-500 font-medium mt-0.5">
                        {getIndustryName(company.parent_company)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {company.subsidiaries && company.subsidiaries.length > 0 && (
              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">
                    Subsidiarias ({company.subsidiaries.length})
                  </h3>
                </div>
                <div className="p-5">
                  <div className="space-y-3">
                    {company.subsidiaries.slice(0, 3).map((subsidiary) => (
                      <div
                        key={subsidiary.id}
                        className="flex items-center justify-between p-3 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer group"
                        onClick={() => onContactClick(subsidiary)}
                      >
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-9 w-9 border border-white shadow-sm ring-1 ring-slate-100">
                            <AvatarImage src={subsidiary.logo_url || ""} />
                            <AvatarFallback className="bg-slate-100 text-slate-700 text-xs font-bold">
                              {subsidiary.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-sm text-slate-900">
                              {subsidiary.name}
                            </p>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-slate-500">
                                {getIndustryName(subsidiary)}
                              </span>
                              {subsidiary.size && (
                                <>
                                  <span className="text-xs text-slate-300">
                                    •
                                  </span>
                                  <span className="text-xs text-slate-600">
                                    {subsidiary.size}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {company.subsidiaries.length > 3 && (
                      <p className="text-sm text-slate-500 font-medium text-center pt-2">
                        +{company.subsidiaries.length - 3} más subsidiarias
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
        
        <DialogFooter className="bg-white border-t border-slate-100 p-5 px-6 rounded-b-[24px] flex-shrink-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
          <div className="flex flex-col-reverse sm:flex-row justify-end w-full gap-3">
            <Button
              variant="outline"
              onClick={() => setIsCreateContactModalOpen(true)}
              className="px-4 h-10 rounded-lg border-indigo-200 text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Crear Contacto
            </Button>
            <Button variant="outline" onClick={onClose} className="px-6 h-10 rounded-lg w-full sm:w-auto">
              Cerrar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      <CreateContactModal
        isOpen={isCreateContactModalOpen}
        onClose={() => setIsCreateContactModalOpen(false)}
        onSave={handleCreateContact}
        existingContact={{ companyId: company.id }}
        customerStatuses={customerStatuses}
        existingContacts={[]}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              la empresa "{company.name}" y todos sus datos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


    </Dialog>
  );
}
